import { memo, useEffect, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import type { AgendaDayDTO, AgendaEventDTO } from './auth/apiClient.js';

/**
 * The Agenda — a day view of every event across the user's linked Google calendars, in the right-hand
 * drawer. All-day items sit above a time-ordered list of meetings; each row is tinted by its Google
 * color (or, lacking one, by its source calendar) and shows the time range, attendee count, and a
 * Meet link when present. On "today" past events dim and the in-progress one is accented. Purely
 * presentational: loading/refresh, day navigation, and the viewed date all live in App.
 */
export interface AgendaPanelProps {
  loading: boolean;
  error: string | null;
  day: AgendaDayDTO | null;
  /** The date being viewed (YYYY-MM-DD), so the header reads right before data loads. */
  dateKey: string;
  /** Whether `dateKey` is the client's current local day (enables the "now" treatment). */
  isToday: boolean;
  onRefresh: () => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onToday: () => void;
  /** Open (or create + bind) the meeting note for an event — binds it by eventId for the initiative bridge. */
  onOpenEventNote?: (eventId: string, title: string) => void;
}

/** Google Calendar's event `colorId` palette → hex, so tints match what the user sees in Google. */
const GOOGLE_EVENT_COLORS: Record<string, string> = {
  '1': '#7986cb', // Lavender
  '2': '#33b679', // Sage
  '3': '#8e24aa', // Grape
  '4': '#e67c73', // Flamingo
  '5': '#f6bf26', // Banana
  '6': '#f4511e', // Tangerine
  '7': '#039be5', // Peacock
  '8': '#616161', // Graphite
  '9': '#3f51b5', // Blueberry
  '10': '#0b8043', // Basil
  '11': '#d50000', // Tomato
};

/** Fallback palette when an event carries no colorId — assigned per source calendar so accounts differ. */
const ACCOUNT_COLORS = ['#039be5', '#0b8043', '#8e24aa', '#f4511e', '#3f51b5', '#e67c73'];

/** Stable color for an event: its Google color if set, else a per-account color hashed from the email. */
function eventColor(e: AgendaEventDTO): string {
  if (e.colorId && GOOGLE_EVENT_COLORS[e.colorId]) return GOOGLE_EVENT_COLORS[e.colorId]!;
  const key = e.accountEmail ?? e.accountId;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return ACCOUNT_COLORS[h % ACCOUNT_COLORS.length]!;
}

/** "9:00 AM" in the viewer's local zone; "" if the ISO doesn't parse. */
function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Header label for the viewed day, e.g. "Wed, Jul 2". Parses the dateKey as local midnight. */
function dayLabel(dateKey: string): string {
  const [y, m, d] = dateKey.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function EventRow({
  event,
  now,
  isToday,
  onOpenEventNote,
}: {
  event: AgendaEventDTO;
  now: number;
  isToday: boolean;
  onOpenEventNote?: (eventId: string, title: string) => void;
}): ReactElement {
  const color = eventColor(event);
  const startMs = new Date(event.start).getTime();
  const endMs = new Date(event.end).getTime();
  const past = isToday && !event.endUnspecified && Number.isFinite(endMs) && endMs <= now;
  const current = isToday && Number.isFinite(startMs) && startMs <= now && (event.endUnspecified || (Number.isFinite(endMs) && now < endMs));
  const range = event.endUnspecified ? clockTime(event.start) : `${clockTime(event.start)} – ${clockTime(event.end)}`;
  return (
    <div className="lapo-ag-row" data-past={past ? 'true' : 'false'} data-current={current ? 'true' : 'false'}>
      <span className="lapo-ag-bar" style={{ background: color }} />
      <div className="lapo-ag-body">
        <div className="lapo-ag-title">
          {event.title || '(no title)'}
          {current && <span className="lapo-ag-now">Now</span>}
        </div>
        <div className="lapo-ag-meta">
          <span className="lapo-ag-time">{range}</span>
          {event.attendeeCount > 0 && (
            <span className="lapo-ag-att">
              <MaterialIcon name="group" size={12} />
              {event.attendeeCount}
            </span>
          )}
          {event.accountEmail && <span className="lapo-ag-cal" title={event.accountEmail}>{event.accountEmail}</span>}
        </div>
      </div>
      {onOpenEventNote && (
        <button
          type="button"
          className="lapo-ag-meet"
          title="Open meeting notes"
          aria-label="Open meeting notes"
          onClick={(e) => {
            e.stopPropagation();
            onOpenEventNote(event.id, event.title || 'Meeting notes');
          }}
        >
          <MaterialIcon name="description" size={18} />
        </button>
      )}
      {event.hangoutLink && (
        <a
          className="lapo-ag-meet lapo-ag-meet-video"
          href={event.hangoutLink}
          target="_blank"
          rel="noreferrer"
          title="Join Google Meet"
          aria-label="Join Google Meet"
          onClick={(e) => e.stopPropagation()}
        >
          <MaterialIcon name="videocam" size={18} />
        </a>
      )}
    </div>
  );
}

// Memoized (audit F3): App re-renders on every agenda "now" tick / resize / keystroke; a stable-prop
// panel shouldn't re-render with it. Hoisted function so the export can wrap it above the definition.
export const AgendaPanel = memo(AgendaPanelBase);

function AgendaPanelBase({
  loading,
  error,
  day,
  dateKey,
  isToday,
  onRefresh,
  onPrevDay,
  onNextDay,
  onToday,
  onOpenEventNote,
}: AgendaPanelProps): ReactElement {
  // "Now" ticks each minute INSIDE the panel (audit F4): the panel is only mounted while the Agenda
  // drawer is open, so the tick re-renders just this subtree — previously it lived in App state and
  // re-rendered the whole app every minute.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const events = day?.events ?? [];
  const allDay = events.filter((e) => e.allDay);
  const timed = events.filter((e) => !e.allDay).sort((a, b) => a.start.localeCompare(b.start));
  const empty = !loading && !error && day && events.length === 0;
  return (
    <div className="lapo-ag">
      <div className="lapo-ag-head">
        <div className="lapo-ag-nav">
          <button type="button" className="lapo-icon-btn" title="Previous day" aria-label="Previous day" onClick={onPrevDay}>
            <MaterialIcon name="chevron_left" size={18} />
          </button>
          <button
            type="button"
            className="lapo-ag-date"
            title="Jump to today"
            onClick={onToday}
            data-today={isToday ? 'true' : 'false'}
          >
            {isToday ? 'Today' : dayLabel(dateKey)}
          </button>
          <button type="button" className="lapo-icon-btn" title="Next day" aria-label="Next day" onClick={onNextDay}>
            <MaterialIcon name="chevron_right" size={18} />
          </button>
        </div>
        <button
          type="button"
          className="lapo-icon-btn"
          title={loading ? 'Refreshing…' : 'Refresh'}
          aria-label={loading ? 'Refreshing' : 'Refresh'}
          disabled={loading}
          onClick={onRefresh}
        >
          <MaterialIcon name={loading ? 'progress_activity' : 'refresh'} size={18} className={loading ? 'lapo-td-spin' : undefined} />
        </button>
      </div>
      {!isToday && <div className="lapo-ag-subhead">{dayLabel(dateKey)}</div>}
      <div className="lapo-ag-scroll">
        {loading && !day && <div className="lapo-ag-note">Loading…</div>}
        {error && <div className="lapo-ag-note lapo-ag-error">{error}</div>}
        {empty && <div className="lapo-ag-note">Nothing on the calendar. 🎉</div>}
        {allDay.length > 0 && (
          <section className="lapo-ag-group">
            {timed.length > 0 && <h3 className="lapo-ag-group-title">All-day</h3>}
            {allDay.map((e) => (
              <div key={e.id} className="lapo-ag-allday">
                <span className="lapo-ag-bar" style={{ background: eventColor(e) }} />
                <span className="lapo-ag-title">{e.title || '(no title)'}</span>
              </div>
            ))}
          </section>
        )}
        {timed.length > 0 && (
          <section className="lapo-ag-group">
            {timed.map((e) => (
              <EventRow key={e.id} event={e} now={now} isToday={isToday} onOpenEventNote={onOpenEventNote} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
